/**
 * Heading Animations — Horizon Pro
 * GSAP ScrollTrigger-powered text animations for section headings.
 * Reads global settings from body data attributes.
 * Individual headings can override via data-hp-heading-anim="style".
 * Respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  /* ── Bail: reduced motion ── */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* ── Wait for GSAP + ScrollTrigger ── */
  function waitForGSAP(cb) {
    if (window.gsap && window.ScrollTrigger) return cb();
    var id = setInterval(function () {
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(id);
        cb();
      }
    }, 50);
  }

  waitForGSAP(function () {
    gsap.registerPlugin(ScrollTrigger);

    var body = document.body;
    var globalStyle = body.dataset.hpHeadingConfig || 'none';
    var speedMap = { slow: 1.2, normal: 0.8, fast: 0.45 };
    var speed = speedMap[body.dataset.hpHeadingSpeed] || 0.8;
    var staggerBase = parseFloat(body.dataset.hpHeadingStagger) || 0.03;

    if (globalStyle === 'none') return;

    function splitText(el, mode) {
      var original = el.textContent;
      el.setAttribute('data-hp-original-text', original);
      el.setAttribute('aria-label', original);

      if (mode === 'char') {
        var segments = original.split(/(\s+)/);
        el.innerHTML = segments
          .map(function (segment) {
            if (/^\s+$/.test(segment)) return ' ';
            var chars = segment.split('').map(function (c) {
              return '<span class="hp-anim-char" style="display:inline-block">' + c + '</span>';
            }).join('');
            return '<span style="display:inline-block;white-space:nowrap">' + chars + '</span>';
          })
          .join('');
        return el.querySelectorAll('.hp-anim-char');
      }

      var words = original.split(/\s+/);
      el.innerHTML = words
        .map(function (w) {
          return '<span class="hp-anim-word" style="display:inline-block;clip-path:inset(0 0 0 0)"><span class="hp-anim-word-inner" style="display:inline-block">' + w + '</span></span>';
        })
        .join(' ');
      return el.querySelectorAll('.hp-anim-word-inner');
    }

    var scrambleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';

    var animations = {
      'fade-up': function (el) {
        gsap.fromTo(el, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: speed, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      },

      'letter-reveal': function (el) {
        var chars = splitText(el, 'char');
        gsap.set(chars, { opacity: 0, y: 20 });
        gsap.to(chars, { opacity: 1, y: 0, duration: speed * 0.6, stagger: staggerBase, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      },

      'word-slide': function (el) {
        var words = splitText(el, 'word');
        gsap.set(words, { y: '110%' });
        gsap.to(words, { y: '0%', duration: speed, stagger: staggerBase * 3, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      },

      'kinetic-split': function (el) {
        var chars = splitText(el, 'char');
        chars.forEach(function (c, i) {
          var xDir = (i % 2 === 0 ? -1 : 1) * (40 + Math.random() * 60);
          var yDir = -30 + Math.random() * 60;
          var rot = (Math.random() - 0.5) * 90;
          gsap.set(c, { opacity: 0, x: xDir, y: yDir, rotation: rot, scale: 0.5 });
        });
        gsap.to(chars, { opacity: 1, x: 0, y: 0, rotation: 0, scale: 1, duration: speed * 0.8, stagger: staggerBase * 0.8, ease: 'back.out(1.4)', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      },

      typewriter: function (el) {
        var chars = splitText(el, 'char');
        gsap.set(chars, { opacity: 0 });
        var cursor = document.createElement('span');
        cursor.className = 'hp-anim-cursor';
        cursor.textContent = '|';
        cursor.style.cssText = 'display:inline-block;animation:hp-cursor-blink 0.6s steps(2) infinite;margin-left:1px;';
        el.appendChild(cursor);
        var tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
        tl.to(chars, { opacity: 1, duration: 0.05, stagger: staggerBase * 1.5, ease: 'none' });
        tl.to(cursor, { opacity: 0, delay: 1.5, duration: 0.3, onComplete: function () { cursor.remove(); } }, '>');
      },

      'blur-in': function (el) {
        gsap.fromTo(el, { opacity: 0, filter: 'blur(12px)' }, { opacity: 1, filter: 'blur(0px)', duration: speed * 1.2, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      },

      'clip-reveal': function (el) {
        gsap.set(el, { clipPath: 'inset(0 100% 0 0)' });
        gsap.to(el, { clipPath: 'inset(0 0% 0 0)', duration: speed * 1.2, ease: 'power3.inOut', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      },

      'text-scramble': function (el) {
        var original = el.textContent;
        el.setAttribute('data-hp-original-text', original);
        el.setAttribute('aria-label', original);

        var chars = original.split('');
        var resolved = new Array(chars.length).fill(false);
        var current = chars.map(function (c) {
          return c === ' ' ? ' ' : scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
        });

        chars.forEach(function (c, i) {
          if (c === ' ') resolved[i] = true;
        });

        el.textContent = current.join('');
        gsap.set(el, { opacity: 1 });

        var totalDuration = speed * 1.8;
        var resolveDelay = totalDuration * 0.25;
        var scrambleInterval;

        ScrollTrigger.create({
          trigger: el,
          start: 'top 90%',
          once: true,
          onEnter: function () {
            var startTime = Date.now();
            scrambleInterval = setInterval(function () {
              var elapsed = (Date.now() - startTime) / 1000;
              for (var i = 0; i < chars.length; i++) {
                if (resolved[i]) continue;
                var resolveTime = resolveDelay + (i / chars.length) * (totalDuration - resolveDelay);
                if (elapsed >= resolveTime) {
                  current[i] = chars[i];
                  resolved[i] = true;
                } else {
                  current[i] = scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
                }
              }
              el.textContent = current.join('');
              if (resolved.every(Boolean)) {
                clearInterval(scrambleInterval);
                el.textContent = original;
              }
            }, 35);
          }
        });
      }
    };

    function animateHeading(el) {
      if (el.dataset.hpAnimApplied) return;
      el.dataset.hpAnimApplied = 'true';
      var style = el.dataset.hpHeadingAnim || globalStyle;
      if (style === 'none' || style === 'default') return;
      var fn = animations[style];
      if (fn) fn(el);
    }

    function observeHeadings(root) {
      var headings = root.querySelectorAll('[data-hp-heading-anim]:not([data-hp-anim-applied])');
      headings.forEach(animateHeading);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { observeHeadings(document); });
    } else {
      observeHeadings(document);
    }

    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute && node.hasAttribute('data-hp-heading-anim')) animateHeading(node);
          if (node.querySelectorAll) observeHeadings(node);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', function (e) {
      if (e.target) observeHeadings(e.target);
    });
  });
})();
